import { useState, useEffect, useCallback, useRef } from 'preact/hooks'
import type { CloudNote } from '../../types'
import { NOTE_TYPE_LABELS } from '../../types'
import { listCloudNotes, deleteCloudNote, downloadBlobAsUrl, downloadBlobAsText, listBlobKeys, getBlobDirectUrls } from '../../services/api'
import { formatDate, EmptyState, DetailRow, detailPanelStyle, refreshBtnStyle, tabStyle, slugifyFilename, extensionForMime, triggerDownloadFromUrl, triggerDownloadText } from './shared'
import { FullscreenImageViewer } from '../FullscreenImageViewer'
import { ConfirmDialog, DeleteButton } from './CrudHelpers'
import { ReportContentDialog, isAiCreatedNote } from '../ReportContentDialog'

type FilesTab = 'images' | 'documents' | 'voice' | 'all'

/** Cache of loaded blob URLs to avoid re-downloading */
const blobCache = new Map<string, { url: string; mimeType: string }>()

/**
 * The image grid mounts ~100 thumbnails at once. Firing a blob/url (+ blob/list)
 * per tile blew through ginger's nginx limit (10 r/s, burst 20) — most tiles got
 * 429 and rendered "Not in cloud" although the blobs exist. So: presign every
 * known key in batches up front (see prefetchPresignedUrls), and cap how many
 * tiles load concurrently.
 */
const MAX_CONCURRENT_THUMBS = 4
let activeThumbLoads = 0
const thumbQueue: Array<() => void> = []

async function withThumbSlot<T>(fn: () => Promise<T>): Promise<T> {
  if (activeThumbLoads >= MAX_CONCURRENT_THUMBS) {
    await new Promise<void>((resolve) => thumbQueue.push(resolve))
  }
  activeThumbLoads++
  try {
    return await fn()
  } finally {
    activeThumbLoads--
    thumbQueue.shift()?.()
  }
}

/** Presign all attachment keys (thumb + full) with the batch endpoint, 100 per call. */
async function prefetchPresignedUrls(notes: CloudNote[]): Promise<void> {
  const keys = new Set<string>()
  for (const n of notes) {
    const thumb = getThumbnailKey(n)
    if (thumb) keys.add(thumb)
    const full = getBlobKey(n)
    if (full && isCloudBlobKey(full)) keys.add(full)
  }
  const all = [...keys]
  for (let i = 0; i < all.length; i += 100) {
    await getBlobDirectUrls(all.slice(i, i + 100))
  }
}

/** Download a blob as a displayable URL, caching only successes — a failure may be
 *  transient (rate limit, network) and must not stick as "Not in cloud". */
async function loadBlobUrl(key: string): Promise<{ url: string; mimeType: string } | null> {
  const cached = blobCache.get(key)
  if (cached) return cached
  const result = await downloadBlobAsUrl(key)
  if (result) blobCache.set(key, result)
  return result
}

/** Normalize camelCase fields from Flutter to snake_case used by the web client */
function normalizeNote(n: CloudNote): CloudNote {
  const r = n as any
  if (r.isAiGeneratedDocument != null && n.is_ai_generated_document == null) n.is_ai_generated_document = r.isAiGeneratedDocument
  if (r.isAiGeneratedImage != null && n.is_ai_generated_image == null) n.is_ai_generated_image = r.isAiGeneratedImage
  if (r.isGeneratedPdf != null && n.is_generated_pdf == null) n.is_generated_pdf = r.isGeneratedPdf
  if (r.isApp != null && n.is_app == null) n.is_app = r.isApp
  if (r.createdAt != null && n.created_at == null) n.created_at = r.createdAt
  if (r.lastUpdatedAt != null && n.last_updated_at == null) n.last_updated_at = r.lastUpdatedAt
  // Normalize attachment keys
  if (n.attachments) {
    for (const att of n.attachments) {
      if (att.blobKey && !att.blob_key) att.blob_key = att.blobKey
      if (att.filePath && !att.file_path) att.file_path = att.filePath
      const a = att as any
      if (a.thumbnail_key && !att.metadata) att.metadata = { thumbnailKey: a.thumbnail_key }
      else if (a.thumbnail_key && att.metadata && !(att.metadata as any).thumbnailKey) (att.metadata as any).thumbnailKey = a.thumbnail_key
    }
  }
  return n
}

export function FilesView({ focusId }: { focusId?: string } = {}) {
  const [notes, setNotes] = useState<CloudNote[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<FilesTab>('images')
  const [selectedNote, setSelectedNote] = useState<CloudNote | null>(null)
  const [confirmDeleteNote, setConfirmDeleteNote] = useState<CloudNote | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const handleDelete = useCallback(async () => {
    if (!confirmDeleteNote || deleting) return
    setDeleting(true)
    setDeleteError(null)
    try {
      await deleteCloudNote(confirmDeleteNote.id)
      setNotes((prev) => prev.filter((n) => n.id !== confirmDeleteNote.id))
      if (selectedNote?.id === confirmDeleteNote.id) setSelectedNote(null)
      setConfirmDeleteNote(null)
    } catch (e) {
      setDeleteError((e as Error).message)
    } finally {
      setDeleting(false)
    }
  }, [confirmDeleteNote, deleting, selectedNote])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const loaded = (await listCloudNotes()).map(normalizeNote)
      await prefetchPresignedUrls(loaded.filter((n) => n.is_ai_generated_image === true))
      setNotes(loaded)
    } catch (e) {
      console.error('Failed to load files:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Auto-select the focused file and switch to its tab.
  useEffect(() => {
    if (!focusId || notes.length === 0) return
    const match = notes.find((n) => n.id === focusId)
    if (!match) return
    if (selectedNote?.id !== match.id) {
      let nextTab: FilesTab = 'all'
      if (match.is_ai_generated_image) nextTab = 'images'
      else if (match.is_ai_generated_document || match.is_generated_pdf) nextTab = 'documents'
      else if (match.type === 1) nextTab = 'voice'
      setActiveTab(nextTab)
      setSelectedNote(match)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, notes])

  const images = notes.filter((n) => n.is_ai_generated_image === true)
  const documents = notes.filter((n) => ((n.is_ai_generated_document === true) && !n.is_app) || n.is_generated_pdf === true)
  const voice = notes.filter((n) => n.type === 1)
  const all = [...images, ...documents, ...voice].sort((a, b) => {
    const ta = a.last_updated_at || a.created_at || ''
    const tb = b.last_updated_at || b.created_at || ''
    return tb.localeCompare(ta)
  })

  const current = activeTab === 'images' ? images : activeTab === 'documents' ? documents : activeTab === 'voice' ? voice : all

  const handleSelect = (note: CloudNote) => {
    setSelectedNote(selectedNote?.id === note.id ? null : note)
  }

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(250,250,250,0.1)' }}>
          <button onClick={() => { setActiveTab('images'); setSelectedNote(null) }} style={tabStyle(activeTab === 'images')}>
            Images ({images.length})
          </button>
          <button onClick={() => { setActiveTab('documents'); setSelectedNote(null) }} style={tabStyle(activeTab === 'documents')}>
            Documents ({documents.length})
          </button>
          <button onClick={() => { setActiveTab('voice'); setSelectedNote(null) }} style={tabStyle(activeTab === 'voice')}>
            Voice ({voice.length})
          </button>
          <button onClick={() => { setActiveTab('all'); setSelectedNote(null) }} style={tabStyle(activeTab === 'all')}>
            All ({all.length})
          </button>
          <span style={{ flex: 1 }} />
          <button onClick={load} style={{ ...refreshBtnStyle, margin: '0 8px' }} disabled={loading}>
            {loading ? '...' : 'Refresh'}
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {loading && <div style={{ padding: 24, textAlign: 'center', color: 'rgba(250,250,250,0.4)' }}>Loading...</div>}
          {!loading && current.length === 0 && <EmptyState label="No files found" />}

          {!loading && activeTab === 'images' && images.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
              {images.map((note) => (
                <ImageThumbnail
                  key={note.id}
                  note={note}
                  selected={selectedNote?.id === note.id}
                  onSelect={() => handleSelect(note)}
                />
              ))}
            </div>
          )}

          {!loading && activeTab !== 'images' && current.length > 0 && (
            <div>
              {current.map((note) => (
                <div
                  key={note.id}
                  onClick={() => handleSelect(note)}
                  style={{
                    ...fileRowStyle,
                    cursor: 'pointer',
                    background: selectedNote?.id === note.id ? 'rgba(45,165,160,0.15)' : 'rgba(0,51,68,0.6)',
                  }}
                >
                  <span style={{ fontSize: 20, marginRight: 10 }}>
                    {note.type === 1 ? '\u{1F3A4}' : note.is_ai_generated_image ? '\u{1F5BC}' : note.is_generated_pdf ? '\u{1F4C4}' : '\u{1F4C3}'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {note.title || 'Untitled'}
                    </div>
                    {Array.isArray(note.tags) && note.tags.length > 0 && (
                      <div style={{ fontSize: 11, color: '#4fc3f7', marginTop: 2 }}>{note.tags.join(', ')}</div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(250,250,250,0.35)', flexShrink: 0 }}>
                    {formatDate(note.last_updated_at || note.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail panel */}
      {selectedNote && (
        <FileDetailPanel
          note={selectedNote}
          onClose={() => setSelectedNote(null)}
          onDelete={() => setConfirmDeleteNote(selectedNote)}
        />
      )}

      {confirmDeleteNote && (
        <ConfirmDialog
          message={`Delete "${confirmDeleteNote.title || 'Untitled'}"? This cannot be undone.`}
          confirmLabel={deleting ? 'Deleting…' : 'Delete'}
          onConfirm={handleDelete}
          onCancel={() => { if (!deleting) { setConfirmDeleteNote(null); setDeleteError(null) } }}
        />
      )}
      {deleteError && (
        <div style={{ position: 'fixed', bottom: 16, right: 16, padding: '10px 14px', borderRadius: 8, background: '#f44336', color: '#fff', fontSize: 13, zIndex: 1100 }}>
          Delete failed: {deleteError}
        </div>
      )}
    </div>
  )
}

// ── Image Thumbnail (loads actual image from blob) ────────────────

function ImageThumbnail({ note, selected, onSelect }: { note: CloudNote; selected: boolean; onSelect: () => void }) {
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)

  useEffect(() => {
    setImageUrl(null)
    setLoadFailed(false)
    let cancelled = false

    // Try thumbnail first, fall back to full image (also when the thumb fails)
    withThumbSlot(async () => {
      if (cancelled) return
      const thumbKey = await resolveThumbnailKey(note)
      let result = thumbKey && !cancelled ? await loadBlobUrl(thumbKey) : null
      if (!result && !cancelled) {
        const fullKey = await resolveBlobKey(note)
        if (fullKey && fullKey !== thumbKey && !cancelled) result = await loadBlobUrl(fullKey)
      }
      if (cancelled) return
      if (result) setImageUrl(result.url)
      else setLoadFailed(true)
    })
    return () => { cancelled = true }
  }, [note.id])

  return (
    <div
      onClick={onSelect}
      style={{
        ...imageCardStyle,
        cursor: 'pointer',
        border: selected ? '1px solid #2DA5A0' : '1px solid rgba(250,250,250,0.1)',
      }}
    >
      <div style={{ width: '100%', aspectRatio: '1', background: 'rgba(0,51,68,0.8)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6, overflow: 'hidden' }}>
        {imageUrl ? (
          <img src={imageUrl} alt={note.title || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
        ) : loadFailed ? (
          <div style={{ textAlign: 'center', color: 'rgba(250,250,250,0.25)', fontSize: 11 }}>
            <div style={{ fontSize: 28, marginBottom: 2 }}>{note.is_ai_generated_image ? '\u{1F3A8}' : '\u{1F5BC}'}</div>
            Not in cloud
          </div>
        ) : (
          <div style={{ color: 'rgba(250,250,250,0.3)', fontSize: 12 }}>Loading...</div>
        )}
      </div>
      <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {note.title || 'Untitled'}
      </div>
      <div style={{ fontSize: 11, color: 'rgba(250,250,250,0.35)' }}>
        {formatDate(note.last_updated_at || note.created_at)}
      </div>
    </div>
  )
}

// ── File Detail Panel (loads actual content) ──────────────────────

function FileDetailPanel({ note, onClose, onDelete }: { note: CloudNote; onClose: () => void; onDelete?: () => void }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [blobMime, setBlobMime] = useState<string | null>(null)
  const [htmlContent, setHtmlContent] = useState<string | null>(null)
  const [blobLoading, setBlobLoading] = useState(false)
  const [blobFailed, setBlobFailed] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [reportedId, setReportedId] = useState<string | null>(null)

  useEffect(() => {
    setBlobUrl(null)
    setBlobMime(null)
    setHtmlContent(null)
    setBlobFailed(false)
    setBlobLoading(true)

    const isImage = note.type === 2 || note.is_ai_generated_image === true
    const isDoc = note.is_ai_generated_document === true || note.is_generated_pdf === true
    const isVoice = note.type === 1
    let cancelled = false

    resolveBlobKey(note).then((key) => {
      if (cancelled) return

      if (isVoice && key) {
        // Voice note — download and create audio blob URL
        downloadBlobAsUrl(key).then((result) => {
          if (cancelled) return
          if (result) { setBlobUrl(result.url); setBlobMime(result.mimeType) }
          else setBlobFailed(true)
          setBlobLoading(false)
        })
        return
      }

      if (isImage) {
        if (!key) { setBlobFailed(true); setBlobLoading(false); return }
        loadBlobUrl(key).then((result) => {
          if (cancelled) return
          if (result) { setBlobUrl(result.url); setBlobMime(result.mimeType) }
          else setBlobFailed(true)
          setBlobLoading(false)
        })
      } else if (isDoc || (key && isDocKey(key))) {
        if (key) {
          downloadBlobAsText(key).then((text) => {
            if (cancelled) return
            if (text) setHtmlContent(text)
            else if (note.content) setHtmlContent(note.content)
            else setBlobFailed(true)
            setBlobLoading(false)
          })
        } else if (note.content) {
          setHtmlContent(note.content)
          setBlobLoading(false)
        } else {
          setBlobFailed(true)
          setBlobLoading(false)
        }
      } else if (key) {
        // Unknown type — try based on file extension
        if (isImageKey(key)) {
          loadBlobUrl(key).then((result) => {
            if (cancelled) return
            if (result) { setBlobUrl(result.url); setBlobMime(result.mimeType) }
            else setBlobFailed(true)
            setBlobLoading(false)
          })
        } else {
          downloadBlobAsText(key).then((text) => {
            if (cancelled) return
            if (text) setHtmlContent(text)
            else setBlobFailed(true)
            setBlobLoading(false)
          })
        }
      } else {
        // No blob key found at all
        if (note.content) setHtmlContent(note.content)
        else setBlobFailed(true)
        setBlobLoading(false)
      }
    })

    return () => { cancelled = true }
  }, [note.id])

  const isImage = note.type === 2 || note.is_ai_generated_image
  const isVoice = note.type === 1
  const isAudio = blobMime?.startsWith('audio/') || isVoice

  return (
    <div style={{ ...detailPanelStyle, width: 'auto', flex: 1 }}>
      <div style={{ padding: 16, borderBottom: '1px solid rgba(250,250,250,0.1)', display: 'flex', alignItems: 'center' }}>
        <span style={{ flex: 1, fontWeight: 600, fontSize: 16 }}>{note.title || 'Untitled'}</span>
        {htmlContent && (
          <button
            onClick={() => {
              const content = isMarkdownContent(htmlContent, note) ? markdownToHtml(htmlContent, note.title || 'Document') : htmlContent
              const blob = new Blob([content], { type: 'text/html' })
              window.open(URL.createObjectURL(blob), '_blank')
            }}
            style={{ ...refreshBtnStyle, marginRight: 6 }}
          >
            Open in new tab
          </button>
        )}
        {isImage && blobUrl && (
          <button
            onClick={() => {
              const title = (note.title || 'Image').replace(/[<>]/g, '')
              const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title><style>html,body{margin:0;padding:0;background:#111;height:100%;}body{display:flex;align-items:center;justify-content:center;}img{max-width:100%;max-height:100vh;object-fit:contain;}</style></head><body><img src="${blobUrl}" alt=""/></body></html>`
              const blob = new Blob([html], { type: 'text/html' })
              const url = URL.createObjectURL(blob)
              window.open(url, '_blank', 'popup=yes,width=900,height=700,noopener=no')
            }}
            style={{ ...refreshBtnStyle, marginRight: 6 }}
          >
            Open in popup
          </button>
        )}
        {/* Export — saves the underlying content (image / audio / document) to disk */}
        {(blobUrl || htmlContent) && (
          <button
            onClick={() => {
              const title = note.title || 'Untitled'
              if (isImage && blobUrl) {
                const ext = extensionForMime(blobMime) || 'png'
                triggerDownloadFromUrl(blobUrl, slugifyFilename(title, ext))
                return
              }
              if (isAudio && blobUrl) {
                const ext = extensionForMime(blobMime) || 'mp3'
                triggerDownloadFromUrl(blobUrl, slugifyFilename(title, ext))
                return
              }
              if (note.is_generated_pdf && blobUrl) {
                triggerDownloadFromUrl(blobUrl, slugifyFilename(title, 'pdf'))
                return
              }
              if (htmlContent) {
                const isMd = isMarkdownContent(htmlContent, note)
                const ext = isMd ? 'md' : 'html'
                const mime = isMd ? 'text/markdown' : 'text/html'
                triggerDownloadText(htmlContent, mime, slugifyFilename(title, ext))
                return
              }
              if (blobUrl) {
                const ext = extensionForMime(blobMime) || 'bin'
                triggerDownloadFromUrl(blobUrl, slugifyFilename(title, ext))
              }
            }}
            style={{ ...refreshBtnStyle, marginRight: 6 }}
            title="Download to your computer"
          >
            Export
          </button>
        )}
        {/* Report offensive AI output (Google Play AI content policy) */}
        {isAiCreatedNote(note) && !blobLoading && (
          <button
            onClick={() => setReportOpen(true)}
            disabled={reportedId === note.id}
            style={{ ...refreshBtnStyle, marginRight: 6 }}
            title="Report inappropriate content"
          >
            {reportedId === note.id ? '✓ Reported' : '\u{1F6A9} Report'}
          </button>
        )}
        {onDelete && <DeleteButton onClick={onDelete} />}
        <button onClick={onClose} style={{ ...refreshBtnStyle, marginLeft: 6 }}>X</button>
      </div>
      {reportOpen && (
        <ReportContentDialog
          kind={isImage ? 'image' : note.is_ai_generated_document ? 'document' : 'note'}
          aiContent={
            isImage
              ? (note.content || note.title || '')
              : `${note.title || ''}\n\n${htmlContent || note.content || ''}`
          }
          imageUrl={isImage ? blobUrl : null}
          messageId={note.id}
          onClose={(sent) => {
            setReportOpen(false)
            if (sent) setReportedId(note.id)
          }}
        />
      )}
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {/* Audio player for voice notes */}
        {isAudio && blobUrl && (
          <div style={{ marginBottom: 16, padding: 16, background: 'rgba(0,51,68,0.6)', borderRadius: 8, border: '1px solid rgba(250,250,250,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 28 }}>{'\u{1F3A4}'}</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: 'rgba(250,250,250,0.7)' }}>Voice Recording</span>
            </div>
            <audio controls style={{ width: '100%' }} src={blobUrl}>
              Your browser does not support audio playback.
            </audio>
          </div>
        )}

        {isAudio && blobLoading && (
          <div style={{ padding: 40, textAlign: 'center', color: 'rgba(250,250,250,0.4)' }}>Loading audio...</div>
        )}

        {isAudio && !blobUrl && blobFailed && (
          <div style={{ padding: 24, textAlign: 'center', color: 'rgba(250,250,250,0.35)', background: 'rgba(0,51,68,0.4)', borderRadius: 8, marginBottom: 16 }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>{'\u{1F3A4}'}</div>
            Audio not available in cloud storage
          </div>
        )}

        {/* Image display */}
        {isImage && blobUrl && (
          <div style={{ marginBottom: 16 }}>
            <img
              src={blobUrl}
              alt={note.title || ''}
              onClick={() => setFullscreen(true)}
              title="Click to view fullscreen"
              style={{ width: '100%', borderRadius: 8, maxHeight: 500, objectFit: 'contain', background: 'rgba(0,0,0,0.3)', cursor: 'zoom-in' }}
            />
          </div>
        )}

        {isImage && blobLoading && (
          <div style={{ padding: 40, textAlign: 'center', color: 'rgba(250,250,250,0.4)' }}>Loading image...</div>
        )}

        {isImage && blobFailed && (
          <div style={{ padding: 24, textAlign: 'center', color: 'rgba(250,250,250,0.35)', background: 'rgba(0,51,68,0.4)', borderRadius: 8, marginBottom: 16 }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>{note.is_ai_generated_image ? '\u{1F3A8}' : '\u{1F5BC}'}</div>
            Image not available in cloud storage
          </div>
        )}

        {/* Document display — HTML rendered in iframe, Markdown converted to HTML first */}
        {!isImage && !isAudio && htmlContent && (
          <div style={{ marginBottom: 16 }}>
            <iframe
              srcDoc={isMarkdownContent(htmlContent, note) ? markdownToHtml(htmlContent, note.title || 'Document') : htmlContent}
              sandbox="allow-same-origin"
              style={{
                width: '100%',
                height: 'calc(100dvh - 280px)',
                border: '1px solid rgba(250,250,250,0.1)',
                borderRadius: 8,
                background: '#fff',
              }}
              title={note.title || 'Document'}
            />
          </div>
        )}

        {!isImage && blobLoading && (
          <div style={{ padding: 40, textAlign: 'center', color: 'rgba(250,250,250,0.4)' }}>Loading document...</div>
        )}

        {/* Content / prompt text (for AI images this is often the generation prompt) */}
        {note.content && !htmlContent && !isImage && !isAudio && (
          <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'rgba(250,250,250,0.8)', marginBottom: 16 }}>
            {note.content}
          </div>
        )}

        {/* For AI-generated images: show the prompt that generated them */}
        {isImage && note.content && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(250,250,250,0.5)', marginBottom: 4 }}>Prompt</div>
            <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', color: 'rgba(250,250,250,0.7)', background: 'rgba(0,34,51,0.6)', padding: 8, borderRadius: 6, border: '1px solid rgba(250,250,250,0.08)' }}>
              {note.content}
            </div>
          </div>
        )}

        {/* Transcription for voice notes */}
        {note.transcription && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(250,250,250,0.5)', marginBottom: 4 }}>Transcription</div>
            <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', color: 'rgba(250,250,250,0.7)' }}>{note.transcription}</div>
          </div>
        )}

        {/* Metadata */}
        <div style={{ marginTop: 8 }}>
          <DetailRow label="Type" value={NOTE_TYPE_LABELS[note.type ?? 0]} />
          {note.is_ai_generated_document && <DetailRow label="AI Document" value="Yes" />}
          {note.is_ai_generated_image && <DetailRow label="AI Generated" value="Yes" />}
          {note.is_generated_pdf && <DetailRow label="Generated PDF" value="Yes" />}
          {Array.isArray(note.tags) && note.tags.length > 0 && <DetailRow label="Tags" value={note.tags.join(', ')} />}
          {note.attachments && note.attachments.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(250,250,250,0.5)', marginBottom: 4 }}>
                Attachments ({note.attachments.length})
              </div>
              {note.attachments.map((att, i) => (
                <div key={i} style={{ fontSize: 12, color: 'rgba(250,250,250,0.6)', marginBottom: 2 }}>
                  {att.description || att.file_path || `Attachment ${i + 1}`}
                  {(att.blob_key || att.file_path || att.filePath) && (
                    <span style={{ color: 'rgba(250,250,250,0.3)', marginLeft: 4 }}>
                      ({(att.blob_key || isCloudBlobKey(att.file_path || att.filePath || '')) ? 'cloud' : 'local only'})
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid rgba(250,250,250,0.1)' }}>
            <DetailRow label="Created" value={formatDate(note.created_at)} />
            <DetailRow label="Updated" value={formatDate(note.last_updated_at)} />
          </div>
        </div>
      </div>
      {fullscreen && blobUrl && isImage && (
        <FullscreenImageViewer src={blobUrl} alt={note.title || ''} onClose={() => setFullscreen(false)} />
      )}
    </div>
  )
}

// ── Helpers ────────────────────────────────────────────────────────

/** Check if a path is a cloud blob key (hash/entity_type/entity_id/filename) */
function isCloudBlobKey(path: string): boolean {
  // Cloud blob keys: 38a4a4bee8ddae2a/notes/uuid/filename.ext (hex hash, then entity type)
  // Local absolute paths: /data/user/0/... or C:\Users\...
  // Portable relative paths: users/current_user/images/file.png or documents/abc.html
  if (!path) return false
  if (path.startsWith('/') || path.startsWith('\\')) return false
  const parts = path.split('/')
  // Cloud keys have 4+ segments: hash/entity_type/entity_id/filename
  // and the first segment is a hex hash (16+ chars)
  return parts.length >= 4 && /^[0-9a-f]{8,}$/.test(parts[0])
}

/** Extract the filename from a local device path */
function filenameFromPath(path: string): string {
  if (!path) return ''
  const parts = path.replace(/\\/g, '/').split('/')
  return parts[parts.length - 1] || ''
}

/** Get the first cloud blob key from attachments, or null */
function getBlobKey(note: CloudNote): string | null {
  if (!note.attachments || note.attachments.length === 0) return null
  for (const att of note.attachments) {
    // Check blobKey (camelCase from Flutter) and blob_key (snake_case legacy)
    const key = att.blobKey || att.blob_key
    if (key && typeof key === 'string') return key
    // Fallback: check if filePath is itself a cloud blob key
    const path = att.file_path || att.filePath
    if (path && isCloudBlobKey(path)) return path
  }
  return null
}

/**
 * Resolve a blob key for a note: first check attachments for a cloud key,
 * then fall back to listing blobs from the backend for this note ID.
 * Results are cached.
 */
const resolvedBlobKeys = new Map<string, string | null>()

/** blob/list per note, memoized so the full-key and thumb-key lookups share one
 *  request. An empty result isn't cached: listBlobKeys also returns [] on a
 *  (possibly rate-limited) error, so let a later view retry. */
const noteBlobKeyLists = new Map<string, string[]>()

async function listNoteBlobKeys(noteId: string): Promise<string[]> {
  const cached = noteBlobKeyLists.get(noteId)
  if (cached) return cached
  const keys = await listBlobKeys('notes', noteId)
  if (keys.length > 0) noteBlobKeyLists.set(noteId, keys)
  return keys
}

async function resolveBlobKey(note: CloudNote): Promise<string | null> {
  if (resolvedBlobKeys.has(note.id)) return resolvedBlobKeys.get(note.id) ?? null

  // 1) Check attachments for an existing cloud key
  const directKey = getBlobKey(note)
  if (directKey) {
    resolvedBlobKeys.set(note.id, directKey)
    return directKey
  }

  // 2) Has attachments with local-only paths — ask backend to list blobs for this note
  if (note.attachments && note.attachments.length > 0) {
    const keys = await listNoteBlobKeys(note.id)
    if (keys.length > 0) {
      const key = keys.find((k) => !k.includes('_thumb')) ?? keys[0]
      resolvedBlobKeys.set(note.id, key)
      return key
    }
  }

  return null
}

/**
 * Get the thumbnail blob key from attachment metadata.
 * Mobile app stores it as metadata.thumbnailKey after uploading the thumbnail.
 */
function getThumbnailKey(note: CloudNote): string | null {
  if (!note.attachments || note.attachments.length === 0) return null
  for (const att of note.attachments) {
    // Check top-level thumbnail_key (set by mobile cloud sync)
    const topKey = (att as Record<string, unknown>).thumbnail_key as string | undefined
    if (topKey && isCloudBlobKey(topKey)) return topKey
    // Fallback: check metadata.thumbnailKey
    const meta = att.metadata as Record<string, unknown> | undefined
    const thumbKey = meta?.thumbnailKey as string | undefined
    if (thumbKey && isCloudBlobKey(thumbKey)) return thumbKey
  }
  return null
}

/**
 * Resolve thumbnail key: check attachment metadata, then try _thumb convention via blob listing.
 */
const resolvedThumbKeys = new Map<string, string | null>()

async function resolveThumbnailKey(note: CloudNote): Promise<string | null> {
  if (resolvedThumbKeys.has(note.id)) return resolvedThumbKeys.get(note.id) ?? null

  // 1) Check attachment metadata for explicit thumbnailKey
  const metaKey = getThumbnailKey(note)
  if (metaKey) {
    resolvedThumbKeys.set(note.id, metaKey)
    return metaKey
  }

  // 2) Check blob listing for _thumb files
  const fullKey = await resolveBlobKey(note)
  if (fullKey) {
    const keys = await listNoteBlobKeys(note.id)
    const thumbKey = keys.find((k) => k.includes('_thumb'))
    if (thumbKey) {
      resolvedThumbKeys.set(note.id, thumbKey)
      return thumbKey
    }
    // Listing succeeded and has no thumb: remember that. An empty listing may be
    // a transient error, so leave it unresolved.
    if (keys.length > 0) resolvedThumbKeys.set(note.id, null)
  }

  return null
}

/** Detect if content is Markdown (not HTML) based on attachment metadata or content heuristics */
function isMarkdownContent(content: string, note: CloudNote): boolean {
  // Check attachment mime type / filename
  if (note.attachments && note.attachments.length > 0) {
    for (const att of note.attachments) {
      const path = (att.file_path || att.filePath || '').toLowerCase()
      if (path.endsWith('.md') || path.endsWith('.markdown')) return true
      const mime = (att.metadata as Record<string, unknown>)?.mime_type as string | undefined
      if (mime === 'text/markdown') return true
    }
  }
  // Content heuristic: if it doesn't start with HTML tags but has markdown markers
  const trimmed = content.trim()
  if (trimmed.startsWith('<!') || trimmed.startsWith('<html') || trimmed.startsWith('<head') || trimmed.startsWith('<body')) return false
  // Check for common markdown patterns
  const mdPatterns = /^#{1,6}\s|^\*\*|^- |^\d+\.\s|^```|^\|.*\|/m
  return mdPatterns.test(trimmed)
}

/** Convert basic Markdown to HTML wrapped in a styled document */
function markdownToHtml(md: string, title: string): string {
  let html = md
    // Escape HTML in code blocks first
    .replace(/```(\w*)\n([\s\S]*?)```/g, (_m, lang, code) => {
      const escaped = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      return `<pre><code class="${lang}">${escaped}</code></pre>`
    })
    // Inline code
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Headers
    .replace(/^######\s+(.+)$/gm, '<h6>$1</h6>')
    .replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>')
    .replace(/^####\s+(.+)$/gm, '<h4>$1</h4>')
    .replace(/^###\s+(.+)$/gm, '<h3>$1</h3>')
    .replace(/^##\s+(.+)$/gm, '<h2>$1</h2>')
    .replace(/^#\s+(.+)$/gm, '<h1>$1</h1>')
    // Bold and italic
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Horizontal rule
    .replace(/^---+$/gm, '<hr>')
    // Blockquotes
    .replace(/^>\s+(.+)$/gm, '<blockquote>$1</blockquote>')
    // Unordered lists
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    // Ordered lists
    .replace(/^\d+\.\s+(.+)$/gm, '<li>$1</li>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
    // Images
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width:100%">')

  // Wrap consecutive <li> in <ul>
  html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>')

  // Merge consecutive blockquotes
  html = html.replace(/<\/blockquote>\n<blockquote>/g, '<br>')

  // Paragraphs: wrap remaining lines that aren't already in tags
  html = html.split('\n\n').map(block => {
    const trimmed = block.trim()
    if (!trimmed) return ''
    if (/^<(h[1-6]|ul|ol|li|pre|blockquote|hr|table|div|p)/.test(trimmed)) return trimmed
    return `<p>${trimmed}</p>`
  }).join('\n')

  // Simple table support: | col1 | col2 |
  html = html.replace(/((?:\|.+\|\n?)+)/g, (tableBlock) => {
    const rows = tableBlock.trim().split('\n').filter(r => r.trim())
    if (rows.length < 2) return tableBlock
    const isSep = (r: string) => /^\|[\s-:|]+\|$/.test(r.trim())
    let result = '<table>'
    let inHead = true
    for (const row of rows) {
      if (isSep(row)) { inHead = false; continue }
      const cells = row.split('|').filter((c, i, a) => i > 0 && i < a.length - 1).map(c => c.trim())
      const tag = inHead ? 'th' : 'td'
      result += '<tr>' + cells.map(c => `<${tag}>${c}</${tag}>`).join('') + '</tr>'
      if (inHead) inHead = false
    }
    result += '</table>'
    return result
  })

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1.0">
<title>${title}</title>
<style>
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;line-height:1.6;max-width:900px;margin:0 auto;padding:24px;color:#1a1a1a}
h1,h2,h3,h4,h5,h6{color:#2c3e50;margin-top:1.2em}
pre{background:#f4f4f4;padding:12px;border-radius:4px;overflow-x:auto}
code{background:#f4f4f4;padding:2px 6px;border-radius:3px;font-size:0.9em}
pre code{background:none;padding:0}
table{border-collapse:collapse;width:100%;margin:16px 0}
th,td{border:1px solid #ddd;padding:8px 12px;text-align:left}
th{background:#f0f0f0;font-weight:600}
tr:nth-child(even){background:#fafafa}
blockquote{border-left:4px solid #3498db;margin:16px 0;padding:8px 16px;background:#f8f9fa}
hr{border:none;border-top:1px solid #ddd;margin:24px 0}
ul{padding-left:24px}
a{color:#2980b9}
img{max-width:100%;height:auto}
@media print{body{max-width:100%;padding:0}}
</style></head><body>${html}</body></html>`
}

/** Check if a blob key points to an image file */
function isImageKey(key: string): boolean {
  const lower = key.toLowerCase()
  return lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg') ||
         lower.endsWith('.gif') || lower.endsWith('.webp') || lower.endsWith('.bmp')
}

/** Check if a blob key points to a text/html document */
function isDocKey(key: string): boolean {
  const lower = key.toLowerCase()
  return lower.endsWith('.html') || lower.endsWith('.htm') || lower.endsWith('.txt') ||
         lower.endsWith('.md') || lower.endsWith('.pdf')
}

// ── Styles ─────────────────────────────────────────────────────────

const imageCardStyle: Record<string, string | number> = {
  padding: 8,
  background: 'rgba(0,51,68,0.6)',
  borderRadius: 8,
  border: '1px solid rgba(250,250,250,0.1)',
}

const fileRowStyle: Record<string, string | number> = {
  display: 'flex',
  alignItems: 'center',
  padding: '10px 12px',
  marginBottom: 4,
  background: 'rgba(0,51,68,0.6)',
  borderRadius: 6,
}
